import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { publicViewsService } from '../services/api';
import PropertyGallery from '@/components/PropertyGallery';
import { BedDouble, Bath, Maximize2, Building2, Lock } from 'lucide-react';

interface Property {
  id: string;
  title: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  area_m2: number;
  images?: string[];
}

interface ClientViewItem {
  id: string;
  property_id: string;
  position: number;
  property: Property | null;
}

interface Note {
  id: string;
  property_id: string | null;
  content: string;
  created_at: string;
}

interface ClientView {
  id: string;
  title: string;
  created_at: string;
  items: ClientViewItem[];
  notes: Note[];
}

function PublicSkeleton() {
  return (
    <div className="min-h-[100dvh] bg-background animate-pulse">
      <div className="max-w-3xl mx-auto px-6 py-12 space-y-8">
        <div className="h-5 bg-stone-100 rounded-md w-24" />
        <div className="h-10 bg-stone-100 rounded-lg w-3/4" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-36 bg-stone-100 rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );
}

function PublicClientViewPage() {
  const { token } = useParams<{ token: string }>();
  const [clientView, setClientView] = useState<ClientView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPublicView = useCallback(async () => {
    try {
      const data = await publicViewsService.get(token!);
      setClientView(data);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail || 'Link inválido o expirado');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPublicView();
  }, [loadPublicView]);

  if (loading) return <PublicSkeleton />;

  if (error) {
    return (
      <div className="min-h-[100dvh] bg-background flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-5">
            <Lock className="w-6 h-6 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold tracking-tight mb-2">Acceso denegado</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">{error}</p>
          <p className="text-xs text-muted-foreground/60 mt-3">
            El link puede haber expirado o sido revocado por el agente.
          </p>
        </div>
      </div>
    );
  }

  if (!clientView) return null;

  return (
    <div className="min-h-[100dvh] bg-background">
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Branding */}
        <div className="flex items-center gap-2 mb-10">
          <Building2 className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium tracking-tight text-muted-foreground">Takarah</span>
          <span className="text-muted-foreground/30 text-xs mx-1">·</span>
          <span className="text-xs text-muted-foreground/60">Selección compartida</span>
        </div>

        {/* Title */}
        <div className="mb-10">
          <h1 className="text-3xl font-semibold tracking-tight">{clientView.title}</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {new Date(clientView.created_at).toLocaleDateString('es-MX', {
              day: 'numeric', month: 'long', year: 'numeric',
            })}
          </p>
        </div>

        {/* Properties */}
        <div className="mb-10">
          <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-4">
            Propiedades — {clientView.items.length}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {clientView.items.map((item) => (
              <div key={item.id} className="bg-white rounded-xl border border-border overflow-hidden">
                <div className="h-1 bg-primary/20" />
                <div className="p-5">
                  {item.property ? (
                    <>
                      <div className="mb-3">
                        <PropertyGallery images={item.property.images ?? []} />
                      </div>
                      <h3 className="font-medium text-sm leading-snug line-clamp-2 mb-3">
                        {item.property.title}
                      </h3>
                      <p className="text-xl font-semibold text-primary mb-3">
                        ${item.property.price.toLocaleString('es-MX')}
                        <span className="text-xs font-normal text-muted-foreground ml-1.5">MXN</span>
                      </p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <BedDouble className="w-3.5 h-3.5" />
                          {item.property.bedrooms} rec.
                        </span>
                        <span className="flex items-center gap-1">
                          <Bath className="w-3.5 h-3.5" />
                          {item.property.bathrooms} baños
                        </span>
                        <span className="flex items-center gap-1">
                          <Maximize2 className="w-3.5 h-3.5" />
                          {item.property.area_m2} m²
                        </span>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">Detalles no disponibles</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notes */}
        {clientView.notes.length > 0 && (
          <div>
            <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-4">
              Notas del agente — {clientView.notes.length}
            </h2>
            <div className="space-y-3">
              {clientView.notes.map((note) => (
                <div key={note.id} className="bg-white border border-border rounded-xl p-4">
                  <p className="text-sm leading-relaxed">{note.content}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {new Date(note.created_at).toLocaleString('es-MX', {
                      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                    })}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-16 pt-6 border-t border-border flex items-center gap-2">
          <Building2 className="w-3.5 h-3.5 text-muted-foreground/40" />
          <p className="text-xs text-muted-foreground/40">
            Enviado vía Takarah · Solo lectura
          </p>
        </div>
      </div>
    </div>
  );
}

export default PublicClientViewPage;
